import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, StatTile } from '../../components/ui/Card';
import { InfoTip } from '../../components/ui/InfoTip';
import { SegmentedControl, type TabItem } from '../../components/ui/Tabs';
import { isConstant, Nil, Table, TableFooter, THead, Th, Td, Tr } from '../../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { useAccess } from '../../hooks/useAccess';
import { useSourcingHandoff, useSyncTenderWinners, usePullHiringCompanies } from '../../hooks/useSourcing';
import { SOURCE_LABELS, getSourcingHandoff } from '../../api/sourcing';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import { getReTenderSummary } from '../../api/tenders';
import { DiaryView } from './DiaryView';
import { Input } from '../../components/ui/Input';
import { cleanText, collapseCountryList, countryName, daysAgo, formatEuroCompact, humanize, repairTenderText, stripRawIds } from './format';
import { displayCompanyName } from './labels';
import { groupSourcingRows, reasonLine, type SourcingGroup } from './sourcingGroups';

type View = 'companies' | 'diary';

const VIEWS: readonly TabItem<View>[] = [
  { id: 'companies', label: 'Companies to chase' },
  { id: 'diary', label: 'By contract end' },
];

/** Source types the API adds that the shared label map does not name yet. */
const LOCAL_SOURCE_LABELS: Record<string, string> = {
  re_tender_incumbent: 'Re-tender incumbent',
  re_tender_challenger: 'Re-tender challenger',
};

function sourceLabel(type: string): string {
  return SOURCE_LABELS[type] ?? LOCAL_SOURCE_LABELS[type] ?? humanize(type);
}

/** Short pill wording so two sources sit side by side on one line; the full label is the tooltip. */
const SHORT_SOURCE_LABELS: Record<string, string> = {
  tender_win: 'Tender winner',
  framework_panel: 'Framework panel',
  tender_lost: 'Lost a tender',
  job_posting: 'Hiring bid roles',
  website_visit: 'Website visit',
  manual: 'Manual',
  re_tender_incumbent: 'Re-tender incumbent',
  re_tender_challenger: 'Re-tender challenger',
};

function shortSourceLabel(type: string): string {
  return SHORT_SOURCE_LABELS[type] ?? sourceLabel(type);
}

/** A row's source combination, order-independent, so "tender + panel" and "panel + tender" match. */
function sourceKey(row: { sourceTypes: string[] }): string {
  return [...row.sourceTypes].sort().join('+');
}

/** The combination most rows share, when it is common enough that repeating it on every row is noise. */
function commonSources(rows: SourcingGroup[]): { key: string; types: string[] } | null {
  if (rows.length < 2) return null;
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(sourceKey(r), (counts.get(sourceKey(r)) ?? 0) + 1);
  const [key, n] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]!;
  if (n / rows.length < 0.5) return null;
  const sample = rows.find((r) => sourceKey(r) === key)!;
  return { key, types: [...sample.sourceTypes].sort() };
}

/**
 * The challenger clause is stored as lemlist copy, written to the challenger
 * ("you bid for it last time"). Read here by our own team, so it is turned to
 * the third person; the API does the same for summaries it writes from now on.
 */
function forReader(summary: string): string {
  return stripRawIds(repairTenderText(summary)).replace(/(;\s*)you (bid|already sit|have won)\b/g, '$1they $2');
}

/** Reasons shown before "+N more". */
const REASON_LINES = 3;

function CompanyRow({
  group,
  showIndustry,
  showSize,
  showSources,
  commonKey,
  showLastSeen,
  open,
  onToggle,
}: {
  group: SourcingGroup;
  showIndustry: boolean;
  showSize: boolean;
  showSources: boolean;
  commonKey: string | null;
  showLastSeen: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const row = group.primary;
  // Every entity's reasons, once each: the Randstad entities share some.
  const summaries = [
    ...new Set(
      group.entities.flatMap((e) => e.sources.map((s) => s.summary)).filter((s): s is string => !!s).map(forReader),
    ),
  ];
  const otherNames = group.names.slice(1);
  // One sentence per reason, strongest first, each once across the entities.
  const lines = [
    ...new Set(
      group.entities
        .flatMap((e) => e.sources)
        .sort((a, b) => b.strength - a.strength)
        .map((src) => reasonLine(src))
        .filter((l): l is string => !!l)
        .map(forReader),
    ),
  ];
  const fullText = summaries.join('\n');
  const canExpand = lines.length > REASON_LINES || summaries.length > 0;

  return (
    <Tr onClick={canExpand ? onToggle : undefined}>
      <Td>
        <p className="text-text-primary">{displayCompanyName(repairTenderText(cleanText(row.name)))}</p>
        {row.domain && <p className="text-body-xs text-text-tertiary">{row.domain}</p>}
        {otherNames.length > 0 && (
          <p className="text-body-xs text-text-tertiary">
            {group.entities.length} entities, also {otherNames.map((n) => repairTenderText(cleanText(n))).join(', ')}
          </p>
        )}
      </Td>
      <Td className="text-text-secondary">
        {group.countries.length > 0 ? group.countries.map((c) => countryName(c)).join(', ') : <Nil />}
      </Td>
      {showIndustry && <Td className="text-text-secondary">{row.industry ?? <Nil />}</Td>}
      {showSize && <Td className="tabular-nums text-text-secondary">{row.employeeRange ?? <Nil />}</Td>}
      {showSources && (
        <Td>
          {commonKey !== null && sourceKey(group) === commonKey ? (
            <span title={group.sourceTypes.map(sourceLabel).join(', ')}>
              <Nil />
            </span>
          ) : (
            <div className="flex flex-wrap items-center gap-1" title={group.sourceTypes.map(sourceLabel).join(', ')}>
              {group.sourceTypes.slice(0, 2).map((t) => (
                <Badge key={t}>{shortSourceLabel(t)}</Badge>
              ))}
              {group.sourceTypes.length > 2 && (
                <span title={group.sourceTypes.slice(2).map(sourceLabel).join(', ')}>
                  <Badge>+{group.sourceTypes.length - 2}</Badge>
                </span>
              )}
            </div>
          )}
        </Td>
      )}
      <Td>
        {summaries.length === 0 ? (
          <Nil />
        ) : (
          <div className="flex items-start gap-1">
            {open ? (
              <div className="min-w-0 space-y-0.5 text-body-xs text-text-secondary">
                {summaries.map((s, i) => {
                  const short = collapseCountryList(s);
                  return (
                    <p key={i} title={short.full ?? undefined}>
                      {short.text}
                    </p>
                  );
                })}
              </div>
            ) : (
              <div className="min-w-0 space-y-0.5 text-body-xs text-text-secondary" title={fullText}>
                {lines.slice(0, REASON_LINES).map((l, i) => {
                  // A pasted list of 39 countries reads as "39 countries"; the list is on hover.
                  const short = collapseCountryList(l);
                  return (
                    <p key={i} title={short.full ?? undefined}>
                      {short.text}
                    </p>
                  );
                })}
                {lines.length > REASON_LINES && (
                  <p className="text-text-tertiary">+{lines.length - REASON_LINES} more reason{lines.length - REASON_LINES === 1 ? '' : 's'}</p>
                )}
              </div>
            )}
            {canExpand && (
              <ChevronRight
                className={clsx('mt-0.5 h-3.5 w-3.5 flex-none text-text-muted transition-transform', open && 'rotate-90')}
                aria-hidden
              />
            )}
          </div>
        )}
      </Td>
      <Td
        align="right"
        className="tabular-nums text-text-primary last:!pr-2"
        title={group.entities.length > 1 ? 'The strongest entity’s strength; entities are not added up' : undefined}
      >
        {group.totalStrength}
      </Td>
      {showLastSeen && (
        <Td
          align="right"
          className="whitespace-nowrap text-body-xs text-text-tertiary last:!pr-2"
          title={formatDayMonthYear(group.lastSeenAt)}
        >
          {daysAgo(group.lastSeenAt)}
        </Td>
      )}
    </Tr>
  );
}

/** Admin-only: the two manual refreshes (both also run every Monday). */
function RefreshControls() {
  const syncTenders = useSyncTenderWinners();
  const pullHiring = usePullHiringCompanies();
  const busy = syncTenders.isPending || pullHiring.isPending;

  const onPullHiring = () => {
    const ok = window.confirm(
      'Pull companies hiring bid roles from TheirStack?\n\nTheirStack bills per job returned. This is bounded to ~25 jobs. Continue?',
    );
    if (ok) pullHiring.mutate({});
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => syncTenders.mutate()} disabled={busy}>
          {syncTenders.isPending ? 'Syncing…' : 'Sync tender winners'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onPullHiring} disabled={busy}>
          {pullHiring.isPending ? 'Pulling…' : 'Pull companies hiring (credits)'}
        </Button>
      </div>
      {syncTenders.isError && <ErrorState title="Tender sync failed" error={syncTenders.error} compact />}
      {syncTenders.data && (
        <Alert variant="success">
          Tenders: pulled {syncTenders.data.awards.awardRows} award rows ({syncTenders.data.awards.upserted} new), added{' '}
          {syncTenders.data.sourced.tenders.written} tender-winner, {syncTenders.data.sourced.frameworkPanels.written}{' '}
          framework-panel, {syncTenders.data.sourced.lostBids.written} lost-bid and{' '}
          {syncTenders.data.sourced.websiteVisits.written} website-visit companies.
        </Alert>
      )}
      {pullHiring.isError && (
        <ErrorState
          title="Hiring pull failed"
          error={pullHiring.error}
          details="Is TheirStack connected in Settings → Integrations?"
          compact
        />
      )}
      {pullHiring.data && (
        <Alert variant="success">
          Hiring: found {pullHiring.data.found} companies, added {pullHiring.data.written} to the list.
        </Alert>
      )}
    </div>
  );
}

function CompaniesToChase() {
  const { isAdmin } = useAccess();
  const { data, isLoading, isError, error, refetch } = useSourcingHandoff();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Strongest first; the API's own order is kept for ties.
  const allRows = useMemo(
    () => [...(data?.rows ?? [])].sort((a, b) => b.totalStrength - a.totalStrength),
    [data],
  );
  // Search over the name, domain, country and every reason, so "Randstad",
  // "facility" or "Ireland" all find what a rep means.
  // The list holds the top 500; a search runs on the server over every company.
  const query = search.trim();
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);
  const searching = query.length > 0;
  const searchResult = useQuery({
    queryKey: ['gtm-sourcing', 'handoff', 'search', debounced],
    queryFn: () => getSourcingHandoff(undefined, debounced),
    enabled: debounced.length > 0,
  });
  const searchSettled = searching && debounced === query && searchResult.isSuccess;
  // One row per company: legal entities folded by parent brand or domain, as Plays does.
  const rows = useMemo(() => {
    if (!searching) return groupSourcingRows(allRows);
    if (!searchSettled || !searchResult.data) return [];
    return groupSourcingRows(searchResult.data.rows);
  }, [allRows, searching, searchSettled, searchResult.data]);
  const entityCount = rows.reduce((n, g) => n + g.entities.length, 0);
  const searchedCount = searchResult.data?.searched ?? data?.total ?? allRows.length;
  const pager = usePagination(rows, 15);
  // Firmographics are often not enriched yet; a column of dashes is noise.
  const showIndustry = rows.some((r) => !!r.primary.industry);
  const showSize = rows.some((r) => !!r.primary.employeeRange);
  // Said once in the header when every row shares it.
  const lastSeenConstant = isConstant(rows, (r) => daysAgo(r.lastSeenAt));
  const sourcesConstant = isConstant(rows, sourceKey);
  const common = commonSources(rows);
  const commonLabel = common ? common.types.map(sourceLabel).join(' + ') : null;
  // One line: what the list is. How 3,065 sourced entities become the companies shown is in the tile and the tip.
  const description = data
    ? [
        searching
          ? searchSettled
            ? `${formatNumber(searchResult.data?.total ?? entityCount)} of ${formatNumber(searchedCount)} sourced entities match “${query}”${
                (searchResult.data?.total ?? 0) > entityCount ? `, top ${formatNumber(entityCount)} shown` : ''
              } · ${formatNumber(rows.length)} compan${rows.length === 1 ? 'y' : 'ies'}`
            : `searching all ${formatNumber(searchedCount)} sourced entities…`
          : `${formatNumber(rows.length)} companies, strongest first`,
        commonLabel && (sourcesConstant ? `all from ${commonLabel}` : `${commonLabel} unless shown`),
        lastSeenConstant && rows[0] ? `all last seen ${daysAgo(rows[0].lastSeenAt)}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;
  const colWidths = [
    '24%',
    '8rem',
    ...(showIndustry ? ['9rem'] : []),
    ...(showSize ? ['6rem'] : []),
    ...(sourcesConstant ? [] : ['16rem']),
    'auto',
    '6.5rem',
    ...(lastSeenConstant ? [] : ['6.5rem']),
  ];

  return (
    <Card padding="sm">
      <CardHeader
        title="Companies to chase"
        description={description}
        action={
          <div className="flex items-start gap-2">
            {allRows.length > 0 && (
              <div className="w-64">
                <Input
                  type="search"
                  placeholder="Company, reason or country"
                  aria-label="Search the sourcing list"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="px-3 py-1.5 text-body-xs"
                />
              </div>
            )}
            {isAdmin && <RefreshControls />}
            <InfoTip>
              Tender winners, framework panels (every supplier admitted alongside its competitors) and named losing
              bidders come from public procurement data. A framework published as one notice per lot (OCRE went out as
              39 country notices) counts once, and a supplier’s country is its own registered address, not the
              buyer’s or the lot’s. Companies hiring bid or proposal roles come from TheirStack.
              Sourcing proposes companies; it contacts no one. A company’s legal entities share one row. Click a row
              to read every reason in full.
            </InfoTip>
          </div>
        }
      />

      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState title="Couldn’t load the sourcing list" error={error} onRetry={() => refetch()} />
      ) : allRows.length === 0 ? (
        <EmptyState title="Nothing sourced yet" description="The list fills on the Monday refresh." />
      ) : searching && searchResult.isError ? (
        <ErrorState title="Couldn’t search the sourcing list" error={searchResult.error} onRetry={() => searchResult.refetch()} />
      ) : searching && !searchSettled ? (
        <LoadingState rows={4} label={`Searching all ${formatNumber(searchedCount)} companies…`} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={`No company matches “${query}”`}
          description={`Searched all ${formatNumber(searchedCount)} companies by name, domain, country and reason.`}
          compact
        />
      ) : (
        <>
          <Table minWidth={820} colWidths={colWidths}>
            <THead>
              <tr>
                <Th>Company</Th>
                <Th>Country</Th>
                {showIndustry && <Th>Industry</Th>}
                {showSize && <Th>Size</Th>}
                {!sourcesConstant && <Th>Sources</Th>}
                <Th>Why it’s a lead</Th>
                <Th align="right" className={lastSeenConstant ? 'last:!pr-2' : undefined}>
                  Strength ↓
                </Th>
                {!lastSeenConstant && (
                  <Th align="right" className="last:!pr-2">
                    Last seen
                  </Th>
                )}
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((group) => (
                <CompanyRow
                  key={group.primary.identityKey}
                  group={group}
                  showIndustry={showIndustry}
                  showSize={showSize}
                  showSources={!sourcesConstant}
                  commonKey={common?.key ?? null}
                  showLastSeen={!lastSeenConstant}
                  open={openKey === group.primary.identityKey}
                  onToggle={() => setOpenKey((k) => (k === group.primary.identityKey ? null : group.primary.identityKey))}
                />
              ))}
            </tbody>
          </Table>
          {data && !searching && (
            <TableFooter
              action={
                <InfoTip label="What is not counted">
                  Retired: every reason the company was sourced for has lapsed (out of the re-tender window, or no longer in
                  the tender rollup once lots count once). Placeholders are notice text, not a company (“Zie bijgevoegd
                  document”, “See attached”). Both stay in the ledger.
                </InfoTip>
              }
            >
              {sourcingReconcile(data, rows.length)}
            </TableFooter>
          )}
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            total={pager.total}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
          />
        </>
      )}
    </Card>
  );
}

/**
 * The one sentence that ties the tile, the list and what is left out together:
 * "3,065 sourced entities not yet tracked: the strongest 500 are listed as 459
 * companies, and search covers all 3,065; not counted: 260 retired, 2 placeholders."
 */
function sourcingReconcile(
  data: { count: number; total?: number; hiddenRetired?: number; hiddenPlaceholders?: number },
  companies: number,
): string {
  const total = data.total ?? data.count;
  const notCounted = [
    (data.hiddenRetired ?? 0) > 0 ? `${formatNumber(data.hiddenRetired ?? 0)} with every reason retired` : null,
    (data.hiddenPlaceholders ?? 0) > 0
      ? `${formatNumber(data.hiddenPlaceholders ?? 0)} placeholder name${data.hiddenPlaceholders === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean);
  const listed =
    total > data.count
      ? `the strongest ${formatNumber(data.count)} are listed as ${formatNumber(companies)} companies, and search covers all ${formatNumber(total)}`
      : `all are listed, as ${formatNumber(companies)} companies`;
  return `${formatNumber(total)} sourced entities not yet tracked: ${listed}${
    notCounted.length ? `; not counted: ${notCounted.join(', ')}` : ''
  }.`;
}

/** Answer-first: how many companies, and what is up for re-tender in the next 90 days. */
function SourcingTiles() {
  const handoff = useSourcingHandoff();
  // The companies the list below shows: the strongest entities, folded by parent brand or domain.
  const listed = useMemo(() => groupSourcingRows(handoff.data?.rows ?? []).length, [handoff.data]);
  // Four numbers worked out and cached on the server, not every contract of four months.
  const summary = useQuery({ queryKey: ['tenders', 're-tender-summary'], queryFn: getReTenderSummary });
  const s = summary.data;
  const diaryValue = (v: string) => (summary.isLoading ? '…' : summary.isError || !s ? 'Unavailable' : v);
  const soonCount = s?.contracts ?? 0;
  const eur = s?.eurValue ?? 0;
  const unvalued = s?.unvalued ?? 0;
  const otherCurrency = s?.otherCurrency ?? 0;
  const horizonText = s ? formatDayMonthYear(s.windowTo) : '…';

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <StatTile
        label="Companies to chase"
        value={handoff.isError ? 'Unavailable' : handoff.data ? formatNumber(listed) : '…'}
        sub={
          handoff.data
            ? (() => {
                const total = handoff.data.total ?? handoff.data.count;
                return total > handoff.data.count
                  ? `Shown of ${formatNumber(total)} sourced entities not yet tracked: the strongest ${formatNumber(handoff.data.count)} entities, folded by parent brand into these companies; search covers all ${formatNumber(total)} · refreshed every Monday`
                  : `All ${formatNumber(total)} sourced entities not yet tracked, folded by parent brand into these companies · refreshed every Monday`;
              })()
            : 'Sourced entities, not yet tracked'
        }
      />
      <StatTile
        label="Contracts ending, 90 days"
        value={diaryValue(formatNumber(soonCount))}
        sub={`Public contracts up for re-tender by ${horizonText}${s?.capped ? ' · capped' : ''}`}
      />
      <StatTile
        label="Contract value ending in 90 days (incl. non-bid-team work)"
        value={diaryValue(formatEuroCompact(eur) || '€0')}
        sub={[
          'Every public contract, next 90 days; the diary hides non-bid-team work',
          unvalued > 0 ? `${unvalued} with no published value` : null,
          otherCurrency > 0 ? `${otherCurrency} in other currencies` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
    </div>
  );
}

/**
 * GTM → Sourcing.
 *
 * Companies worth chasing that we do not track yet — who won public tenders (a
 * bid desk by definition) and who is hiring bid/proposal roles — plus the
 * re-tender diary, the same tender data seen by the month contracts end.
 */
export function SourcingView() {
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'diary' ? 'diary' : 'companies';

  const setView = (next: View) => {
    const p = new URLSearchParams(params);
    if (next === 'diary') p.set('view', 'diary');
    else p.delete('view');
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-4">
      <SourcingTiles />
      <SegmentedControl options={VIEWS} value={view} onChange={setView} ariaLabel="Sourcing view" />
      {view === 'companies' ? <CompaniesToChase /> : <DiaryView />}
    </div>
  );
}
